// src/utils/services/Careplans/mealPlanAiService.js
import { callOpenAIChat } from '../openaiClient';

/**
 * Safe JSON parser - strips markdown fences and extracts JSON object.
 */
const safeParseJson = (raw) => {
  if (!raw) throw new Error('No content to parse');

  let text = typeof raw === 'string' ? raw : String(raw);
  text = text.trim();

  if (text.startsWith('```')) {
    text = text.replace(/^```[a-zA-Z]*\s*/m, '');
    text = text.replace(/```$/m, '');
    text = text.trim();
  }

  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');

  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    throw new Error('No JSON object found in model response');
  }

  return JSON.parse(text.slice(firstBrace, lastBrace + 1));
};

/**
 * Build clinical + dietary context for meal plan generation.
 */
const buildMealPlanContext = ({
  patient = {},
  allergies = [],
  carePlanResult = {},
  dietaryPreferences = '',
  culturalDiet = '',
}) => {
  const parts = [];

  // Demographics
  parts.push('===== PATIENT DEMOGRAPHICS =====');
  parts.push(`Name: ${patient.fullName || 'N/A'}`);
  parts.push(`Age: ${patient.age || 'N/A'}`);
  parts.push(`Gender: ${patient.gender || 'N/A'}`);
  parts.push('');

  // Allergies
  parts.push('===== ALLERGIES =====');
  if (!allergies || allergies.length === 0) {
    parts.push('No allergy records found.');
  } else {
    allergies.forEach((a, idx) => {
      const line = [
        `ALLERGY #${idx + 1}: ${a.allergy || 'Unknown'}`,
        a.reaction ? `Reaction: ${a.reaction}` : null,
        a.status ? `Status: ${a.status}` : null,
      ]
        .filter(Boolean)
        .join(' | ');
      parts.push(line);
    });
  }
  parts.push('');

  // Dietary preferences
  if (dietaryPreferences.trim()) {
    parts.push('===== DIETARY PREFERENCES / RESTRICTIONS =====');
    parts.push(dietaryPreferences.trim());
    parts.push('');
  }

  // Cultural diet
  if (culturalDiet.trim()) {
    parts.push('===== CULTURAL / REGIONAL DIET =====');
    parts.push(culturalDiet.trim());
    parts.push('');
  }

  // Key data from care plan
  parts.push('===== CARE PLAN SUMMARY =====');
  if (carePlanResult.summary) {
    parts.push(`Summary: ${carePlanResult.summary}`);
  }

  if (carePlanResult.identifiedProblems?.length > 0) {
    parts.push('Identified Problems:');
    carePlanResult.identifiedProblems.forEach((p, i) => {
      parts.push(`  ${i + 1}. ${p.problem} (Severity: ${p.severity || 'N/A'})`);
    });
  }

  if (carePlanResult.lifestyleModifications?.length > 0) {
    parts.push('Lifestyle Modifications:');
    carePlanResult.lifestyleModifications.forEach((lm, i) => {
      parts.push(`  ${i + 1}. [${lm.area}] ${lm.recommendation}`);
    });
  }

  if (carePlanResult.goals?.length > 0) {
    parts.push('Care Plan Goals:');
    carePlanResult.goals.forEach((g, i) => {
      parts.push(`  ${i + 1}. ${g.goal} (Priority: ${g.priority || 'N/A'})`);
    });
  }
  parts.push('');

  let fullText = parts.join('\n');

  const MAX_CHARS = 8000;
  if (fullText.length > MAX_CHARS) {
    fullText =
      fullText.slice(0, MAX_CHARS) +
      '\n\n[TRUNCATED – remaining data omitted for context limit]';
  }

  return fullText;
};

/**
 * Generate a personalized weekly meal plan from clinical data and care plan results.
 *
 * @param {Object} params
 * @param {Object} params.patient - { fullName, age, gender }
 * @param {Array}  params.allergies - AdvancedMD allergies array
 * @param {Object} params.carePlanResult - The AI-generated care plan object
 * @param {string} params.dietaryPreferences - Dietary restrictions (e.g. low-sodium, vegetarian)
 * @param {string} params.culturalDiet - Cultural food traditions (e.g. Colombian, Haitian, Mexican)
 * @returns {Promise<Object>} Structured meal plan
 */
export const generateMealPlanFromAI = async ({
  patient = {},
  allergies = [],
  carePlanResult = {},
  dietaryPreferences = '',
  culturalDiet = '',
}) => {
  const contextText = buildMealPlanContext({
    patient,
    allergies,
    carePlanResult,
    dietaryPreferences,
    culturalDiet,
  });

  if (!contextText.trim()) {
    console.warn('[generateMealPlanFromAI] Empty context');
    return { mealPlan: null };
  }

  const userPrompt = `
You are a board-certified clinical dietitian specialized in medical nutrition therapy and culturally appropriate meal planning.
Your task is to generate a **personalized weekly meal plan** for the patient described below, based on their clinical conditions, allergies, dietary preferences, and cultural food traditions.

All output MUST be in ENGLISH.

You will receive:
- Patient demographics
- Allergy records from the EHR
- Dietary preferences and restrictions
- Cultural / regional food traditions
- A summary of the patient's care plan (conditions, goals, lifestyle modifications)

===== INSTRUCTIONS =====

1. **Analyze** the patient's clinical conditions and design meals that support their care plan goals.
2. **Respect** all allergies – never include any allergen in meals. Provide safe alternatives.
3. **Incorporate** the patient's cultural food traditions into the meal plan when possible.
4. **Apply** dietary restrictions (low-sodium, diabetic-friendly, etc.) to every meal.
5. **Generate** a 7-day meal plan with breakfast, lunch, and dinner for each day.
6. **Classify** key foods into a traffic light system: green (eat freely), yellow (moderate), orange (preferably avoid), red (must avoid completely).

Return ONLY a JSON object with this exact structure:

{
  "overview": "A 2-3 sentence summary of the meal plan approach, dietary focus, and cultural considerations.",

  "nutritionTargets": {
    "calories": "Recommended daily calorie range (e.g. '1800-2000 kcal')",
    "sodium": "Daily sodium limit if applicable (e.g. '<2000mg')",
    "carbs": "Carb guidance if applicable (e.g. '45-60g per meal for diabetic control')",
    "protein": "Protein target if applicable",
    "notes": "Any other nutritional notes"
  },

  "weeklyPlan": [
    {
      "day": "Monday",
      "breakfast": { "meal": "Meal description", "notes": "Nutritional or cultural note" },
      "lunch": { "meal": "Meal description", "notes": "Nutritional or cultural note" },
      "dinner": { "meal": "Meal description", "notes": "Nutritional or cultural note" }
    }
  ],

  "foodsToAvoid": [
    { "food": "Food or ingredient name", "reason": "Why it should be avoided (allergy, condition, etc.)" }
  ],

  "allergySafeAlternatives": [
    { "allergen": "The allergen", "alternatives": "Safe substitutes the patient can use" }
  ],

  "snackSuggestions": [
    "Healthy snack option 1",
    "Healthy snack option 2"
  ],

  "hydrationGuidelines": "Recommended daily fluid intake and any restrictions.",

  "foodTrafficLight": {
    "green": [{ "food": "Food name", "reason": "Why safe to eat freely" }],
    "yellow": [{ "food": "Food name", "reason": "Why moderate consumption" }],
    "orange": [{ "food": "Food name", "reason": "Why preferably avoid" }],
    "red": [{ "food": "Food name", "reason": "Why must avoid completely" }]
  }
}

===== IMPORTANT RULES =====
- Return STRICT JSON ONLY. No markdown, no text outside the JSON.
- NEVER include foods the patient is allergic to. Double-check every meal against the allergy list.
- The weeklyPlan array MUST have exactly 7 entries (Monday through Sunday).
- Keep meals realistic, practical, and affordable.
- Incorporate cultural dishes where clinically appropriate.
- Maximum 6 items in foodsToAvoid and allergySafeAlternatives arrays.
- Maximum 5 snack suggestions.
- The foodTrafficLight arrays should each have 3-6 items. Red must include all allergens.
- If cultural diet is not specified, use general healthy eating guidelines.

===== CLINICAL & DIETARY CONTEXT =====

${contextText}
`;

  const data = await callOpenAIChat({
    model: 'gpt-4o-mini',
    messages: [{ role: 'user', content: userPrompt }],
    temperature: 0.2,
  });

  const content = data?.choices?.[0]?.message?.content;

  if (!content) {
    console.error('[generateMealPlanFromAI] Empty response from OpenAI');
    throw new Error('Empty response from OpenAI');
  }

  try {
    const json = safeParseJson(content);
    return { mealPlan: json };
  } catch (err) {
    console.error('[generateMealPlanFromAI] JSON parse error:', err);
    console.error('[generateMealPlanFromAI] Raw content:', content);
    return { mealPlan: null, rawContent: content };
  }
};
